<template>
  <div class="container">
    <h2 class="mt-0">
      {{ $t('dreamFlatInstallRun') }}
    </h2>
    <div style="scroll-behavior: smooth">
      <div class="main-info">
        <div ref="system-requirements" class="paragraph-block">
          <h3 id="systemRequirement">
            {{ $t('systemRequirement') }}
          </h3>
          <div class="system-requirements__group">
            <div class="system-minimum">
              <div class="title">
                {{ $t('minimumSystemShort') }}
              </div>
              <div class="sub-title-group">
                <span>{{ $t('offerOS') }}</span>
                <div>{{ $t('hcInstallWindows') }}</div>
              </div>
              <div class="sub-title-group">
                <span>{{ $t('processor') }}</span>
                <div>{{ $t('offer4Core') }}</div>
              </div>
              <div class="sub-title-group">
                <span>{{ $t('ram') }}</span>
                <div>{{ $t('sixteenGB') }}</div>
              </div>
              <div class="sub-title-group">
                <span>{{ $t('graphicsCard') }}</span>
                <div>{{ $t('videocardMin') }}</div>
              </div>
              <div class="sub-title-group">
                <span>{{ $t('diskSpace') }}</span>
                <div>{{ $t('10GBHDD') }}</div>
              </div>
            </div>
            <div id="system-requirement" ref="system-requirement">
              <div class="title">
                {{ $t('systemRequirementsShort') }}
              </div>
              <div class="sub-title-group">
                <span>{{ $t('offerOS') }}</span>
                <div>{{ $t('hcInstallWindows') }}</div>
              </div>
              <div class="sub-title-group">
                <span>{{ $t('processor') }}</span>
                <div>{{ $t('offer6Core') }}</div>
              </div>
              <div class="sub-title-group">
                <span>{{ $t('ram') }}</span>
                <div>{{ $t('sixteenGB') }}</div>
              </div>
              <div class="sub-title-group">
                <span>{{ $t('graphicsCard') }}</span>
                <div>{{ $t('videocardRecRequirements') }}</div>
              </div>
              <div class="sub-title-group">
                <span>{{ $t('diskSpace') }}:</span>
                <div>{{ $t('SSDOffer') }}</div>
              </div>
            </div>
          </div>
        </div>
        <div
          id="startup-script"
          ref="startup-script"
          class="paragraph-block"
        >
          <h3 id="installingRunning">
            {{ $t('installingRunning') }}
          </h3>
          <p class="title-download">
            <span>{{ $t('startedDownload') }}</span>
            <DownloadLauncherButton
              icon=""
              :add-version-to-label="false"
              :isSeeSize="false"
              :isGoToTariff="true"
              class="help-center-download-button"
            />
          </p>
          <img
            v-if="locale === 'ru'"
            :src="`${staticUrl}/images/sc-install-download-image.webp`"
            alt=""
            class="article-image"
          />
          <img
            v-else
            :src="`${staticUrl}/images/sc-install-en-download-image.webp`"
            alt=""
            class="article-image"
          />
          <p>
            {{ $t('downloadWindow') }}
          </p>
          <p>
            {{ $t('directX12') }}
          </p>
          <p>
            {{ $t('visualC') }}
          </p>
          <p>
            {{ $t('librariesInstalled') }}
          </p>
          <p>
            {{ $t('openDreamFlatUrComp') }}
          </p>
          <div v-if="locale === 'ru'" class="image-carousel">
            <CustomSlider
              width="100%"
              :images="[
                `${staticUrl}/images/sc-install-10.webp`,
                `${staticUrl}/images/sc-install-11.webp`,
                `${staticUrl}/images/sc-install-12.webp`,
                `${staticUrl}/images/sc-install-13.webp`,
                `${staticUrl}/images/sc-install-14.webp`,
                `${staticUrl}/images/sc-install-15.webp`,
                `${staticUrl}/images/sc-install-16.webp`,
                `${staticUrl}/images/sc-install-17.webp`,
              ]"
            />
          </div>
          <div v-else class="image-carousel">
            <CustomSlider
              width="100%"
              :images="[
                `${staticUrl}/images/sc-install-en-10.webp`,
                `${staticUrl}/images/sc-install-en-11.webp`,
                `${staticUrl}/images/sc-install-en-12.webp`,
                `${staticUrl}/images/sc-install-en-13.webp`,
                `${staticUrl}/images/sc-install-en-14.webp`,
                `${staticUrl}/images/sc-install-en-15.webp`,
                `${staticUrl}/images/sc-install-en-16.webp`,
                `${staticUrl}/images/sc-install-en-17.webp`,
              ]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import DownloadLauncherButton from '~/components/DownloadLauncherButton.vue';
import CustomSlider from '~/components/images/CustomSlider.vue';
import {useHead, useRuntimeConfig} from '#imports';

export default {
  components: {DownloadLauncherButton, CustomSlider},
  setup() {
    const {t, locale} = useI18n();
    const isShowFeedBackModal = ref(false);
    const runtimeConfig = useRuntimeConfig();
    const staticUrl = runtimeConfig.public.vueAppStaticUrl;

    useHead({
      title: `${t('dreamFlatInstallRun')}`,
      meta: [
        {
          name: 'description',
          content: `${t('installDfMetaDesc')}`,
        },
      ],
    });
    useSeoMeta({
      ogTitle: `${t('dreamFlatInstallRun')}`,
      description: `${t('installDfMetaDesc')}`,
      ogDescription: `${t('installDfMetaDesc')}`,
      ogImage: 'https://dreamflat.design/prev.webp',
    });
    const sections = ['system-requirements', 'startup-script'];

    return {
      slide: ref(1),
      locale,
      mobileNavMenu: ref(false),
      isShowFeedBackModal,
      staticUrl,
    };
  },
};
</script>
<style lang="scss" scoped>
@import 'css/help-center/help-center-general-ctyle';

.hide-on-mobile {
  display: none;
}
.system-requirements {
  &__group {
    display: flex;
    gap: $g-10;

    .title {
      @include headline-h4;
      margin-bottom: $m-4;
    }

    .sub-title-group {
      display: flex;
      gap: $g-4;
      justify-content: space-between;
      margin-bottom: $m-2;

      span {
        width: 50%;
        @include launcher-p1;
        color: var(--var-gray-200);
      }
      div {
        text-align: start;
        width: 50%;
        @include launcher-p1;
        color: var(--var-black-200);
      }
    }

    @media (max-width: $md) {
      flex-direction: column;
    }
  }
}
.title-download {
  display: grid;
  grid-template-columns: 1fr max-content;
  column-gap: $g-2;
  margin-bottom: $m-4;

  @media (max-width: $md) {
    flex-direction: column;
    row-gap: $g-5;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
    span {
      grid-column: 1 / 3;
    }
    button {
      grid-column: 1 / 2;
    }
  }
}
</style>
